import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, X } from "lucide-react";

import useAppStore from "@/stores/useAppStore";
import BrandLogo from "./brandLogo";
import Navbar from "./navbar";

const Sidebar = () => {
  const { openMobileMenu, setOpenMobileMenu, sidebarCollapsed, setSidebarCollapsed } = useAppStore();

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        id="default-sidebar"
        initial={false}
        animate={{
          width: sidebarCollapsed ? 80 : 280,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        className="relative hidden h-screen flex-col md:flex"
        aria-label="Sidebar"
      >
        {/* Background */}
        <div className="absolute inset-0 border-r border-[#e5e5e7]/60 bg-white" />

        {/* Content Container */}
        <div className="relative z-10 flex h-full flex-col overflow-hidden">
          {/* Logo Section - Fixed */}
          <div className={`flex-shrink-0 px-5 pt-6 transition-all duration-300 ${sidebarCollapsed ? 'px-3' : 'px-5'}`}>
            <BrandLogo collapsed={sidebarCollapsed} />
          </div>

          {/* Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <Navbar collapsed={sidebarCollapsed} />
          </div>

          {/* Collapse Toggle - Fixed */}
          <div className="flex-shrink-0 border-t border-[#e5e5e7]/60 p-4">
            <motion.button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex w-full items-center justify-center rounded-xl bg-white p-3 text-[#86868b] shadow-sm ring-1 ring-black/[0.04] transition-all hover:bg-[#f5f5f7] hover:text-[#1d1d1f]"
            >
              <motion.div
                animate={{ rotate: sidebarCollapsed ? 180 : 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <ChevronLeft size={18} strokeWidth={2} />
              </motion.div>
            </motion.button>
          </div>
        </div>
      </motion.aside>

      {/* Mobile Sidebar */}
      {/*
        EPS-5120 — menu kapandiktan sonra gorunmez katman ekranda kaliyordu.

        Canli olcum (prod, mobil 390x844, 3/3 tekrar): X ile kapatildiktan sonra
        `fixed inset-0 z-50` katmani DOM'da kaliyor, `opacity: 0` oluyor ama
        `pointer-events: auto` kaldigi icin dokunuslari yutuyor;
        `elementFromPoint` sayfadaki dugme yerine bu katmani donduruyor. Sayfa
        yenilenene kadar duzelmiyor.

        Sebep: `AnimatePresence`in cocugu bir Fragment idi ve icindeki iki
        `motion` elemaninin `key`i yoktu. framer-motion cikis animasyonunu
        ancak KEY'LI DOGRUDAN cocuklarda takip edebiliyor; Fragment'ta takip
        kopunca eleman hic unmount edilmiyor. Replikada ayni paketle 3/3 temiz
        ciktigi icin zamanlamaya bagli bir yaris; Fragment kaldirilinca
        kosullu yaris da ortadan kalkiyor.

        Cozum: iki eleman `key`li DOGRUDAN cocuk olarak veriliyor.
      */}
      <AnimatePresence>
        {openMobileMenu && [
            /* Backdrop */
            <motion.div
              key="mobil-menu-katmani"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, pointerEvents: "auto" }}
              // EPS-5120 (QA turu): cikis boyunca katman gorunmez ama hala
              // dokunus yutuyordu. WebKit'te olculdu (390x844, X ile kapatma):
              // +500ms'de `opacity: 0` / `pointer-events: auto`,
              // `elementFromPoint` ekranin ortasinda sayfa yerine bu katmani
              // donduruyor; ancak +1500ms'de unmount oluyor. Gercek iPhone'da
              // bu pencere daha uzun ve kullanici "hicbir seye tiklanmiyor"
              // diye goruyor. Cozum: cikis hedefinde pointer-events kapatiliyor,
              // boylece animasyon surse de dokunuslari gecirmiyor.
              exit={{ opacity: 0, pointerEvents: "none" }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm md:hidden"
              onClick={() => setOpenMobileMenu(false)}
            />,

            /* Mobile Panel */
            <motion.aside
              key="mobil-menu-paneli"
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1, pointerEvents: "auto" }}
              // Panel de cikis boyunca soldaki 300 px'i yutmasin.
              exit={{ x: -300, opacity: 0, pointerEvents: "none" }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
              className="fixed bottom-0 left-0 top-0 z-50 flex w-[300px] flex-col md:hidden"
            >
              {/*
                Background — EPS-5120 (2. bulgu): menunun arka plani seffafti.

                Sinif `bg-[#f5f5f7]/98` idi. Tailwind'in opaklik olcegi
                0,5,10,...,95,100 adimlarindan olusur; **98 bu olcekte yok**,
                dolayisiyla sinif HIC URETILMIYOR. Yayindaki CSS'te
                `.bg-[#f5f5f7]` kurali bulunmuyor ve tarayicida hesaplanan deger
                `rgba(0, 0, 0, 0)` cikiyor (replikada olculdu). Geriye yalniz
                `backdrop-blur-xl` kaldigi icin menunun altindaki sayfa bulanik
                olarak gorunuyor, menu soluk duruyor ve alt menu basliklari zor
                okunuyor.

                Cozum: opakligi sinifa birakmak yerine dumduz opak zemin.
                `backdrop-blur-xl` de kaldirildi: opak zeminin arkasinda hicbir
                sey gostermiyor, yalnizca bos bir kompozisyon katmani maliyeti
                birakiyordu.
              */}
              <div className="absolute inset-0 border-r border-[#e5e5e7] bg-[#f5f5f7]" />

              {/* Content */}
              <div className="relative z-10 flex h-full flex-col overflow-hidden">
                {/* Header - Fixed */}
                <div className="flex flex-shrink-0 items-center justify-between px-5 pt-6">
                  <BrandLogo collapsed={false} />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setOpenMobileMenu(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#86868b] shadow-sm ring-1 ring-black/[0.04] transition-colors hover:bg-[#f5f5f7] hover:text-[#1d1d1f]"
                  >
                    <X size={18} strokeWidth={2} />
                  </motion.button>
                </div>

                {/* Navigation - Scrollable */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <Navbar collapsed={false} />
                </div>
              </div>
            </motion.aside>,
          ]}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
